import { ArrowLeft, ArrowRight, Check, Eye, EyeOff, LockKeyhole, ShieldCheck, Sparkles } from "lucide-react";
import { FormEvent, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Logo } from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { ApiError, postJson } from "../lib/api";
import type { CourseTier, User } from "../types/api";

export function AuthPage({ mode }: { mode: "login" | "signup" }) {
  const isSignup = mode === "signup";
  const { user, loading: authLoading, refresh } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const selectedPlan = searchParams.get("plan") as CourseTier | null;
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!authLoading && user) return <Navigate to="/dashboard" replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await postJson<{ user: User }>(`/api/auth/${mode}`, isSignup ? { displayName, email, password } : { email, password });
      await refresh();
      showToast(isSignup ? "Your account is ready." : "Welcome back.");
      if (isSignup && (selectedPlan === "A" || selectedPlan === "B")) {
        const checkout = await postJson<{ url: string }>("/api/checkout", { plan: selectedPlan });
        window.location.assign(checkout.url);
        return;
      }
      const statePath = (location.state as { from?: string } | null)?.from;
      const queryPath = searchParams.get("returnTo");
      const returnPath = statePath || (queryPath?.startsWith("/") && !queryPath.startsWith("//") ? queryPath : null);
      navigate(returnPath || "/dashboard", { replace: true });
    } catch (caught) {
      const message = caught instanceof ApiError ? caught.message : "Something went wrong. Please try again.";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-brand-panel">
        <div className="auth-grid" />
        <Link className="back-link" to="/"><ArrowLeft size={17} />Back to home</Link>
        <div className="auth-message">
          <span className="auth-symbol"><span>&lt;/&gt;</span></span>
          <span className="eyebrow">Learn by building</span>
          <h1>{isSignup ? "Turn your ideas into playable systems." : "Keep building from where you stopped."}</h1>
          <p>Focused Roblox Studio lessons, working Luau examples and progress tests—all in one clear path.</p>
          <ul>
            <li><Check />Your progress saves automatically</li>
            <li><Check />Watch lessons on any device</li>
            <li><Check />Copy code straight into Studio</li>
          </ul>
        </div>
        <div className="auth-security"><ShieldCheck size={18} /><span>Secure account & payment access</span></div>
      </div>
      <main className="auth-form-panel">
        <div className="auth-mobile-logo"><Logo /></div>
        <form className="auth-card" onSubmit={submit}>
          <div className="auth-card-heading">
            <span className="auth-lock"><LockKeyhole /></span>
            <h2>{isSignup ? "Create your account" : "Welcome back"}</h2>
            <p>{isSignup ? "Sign up first. You’ll choose and pay for your course securely afterwards." : "Log in to continue your Roblox Studio course."}</p>
          </div>
          {selectedPlan && isSignup && <div className="selected-plan"><Sparkles size={16} /><span>Programmer {selectedPlan} selected</span><Link to="/#plans">Change</Link></div>}
          {error && <div className="form-error" role="alert">{error}</div>}
          {isSignup && (
            <label className="form-field">
              <span>Display name</span>
              <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} autoComplete="name" placeholder="Your name" minLength={2} maxLength={50} required />
            </label>
          )}
          <label className="form-field">
            <span>Email address</span>
            <input value={email} onChange={(event) => setEmail(event.target.value)} type="email" autoComplete="email" placeholder="you@example.com" maxLength={254} required />
          </label>
          <label className="form-field">
            <span>Password</span>
            <div className="password-field">
              <input value={password} onChange={(event) => setPassword(event.target.value)} type={showPassword ? "text" : "password"} autoComplete={isSignup ? "new-password" : "current-password"} placeholder={isSignup ? "At least 10 characters" : "Your password"} minLength={isSignup ? 10 : undefined} maxLength={128} required />
              <button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "Hide password" : "Show password"}>{showPassword ? <EyeOff /> : <Eye />}</button>
            </div>
          </label>
          {isSignup && <p className="password-help">Use 10+ characters with a mix of letters, numbers and symbols.</p>}
          <button className="button button-primary button-wide auth-submit" disabled={submitting}>{submitting ? "Please wait…" : isSignup ? "Create account" : "Log in"}<ArrowRight size={17} /></button>
          <p className="auth-switch">{isSignup ? "Already have an account?" : "New to ForgeCode?"} <Link to={isSignup ? "/login" : "/signup"}>{isSignup ? "Log in" : "Create an account"}</Link></p>
          <small className="terms-copy">By continuing, you agree to the course terms and privacy policy.</small>
        </form>
      </main>
    </div>
  );
}
